import { notifications } from '@mantine/notifications';
import { useCallback, useState } from 'react';
import { create } from 'zustand';

export interface Variable {
  name: string;
  value: string;
}

// Tạo global state với zustand
interface VariablesStore {
  variables: Variable[];
  setVariables: (newVariables: Variable[]) => void;
  addVariable: (variable: Variable) => void;
  updateVariable: (name: string, newData: { name?: string; value?: string }) => void;
  deleteVariable: (name: string) => void;
}

export const useVariablesStore = create<VariablesStore>((set) => ({
  variables: [],
  setVariables: (newVariables) => set({ variables: newVariables }),
  addVariable: (variable) => set((state) => ({ 
    variables: [...state.variables, variable] 
  })),
  updateVariable: (name, newData) => set((state) => ({
    variables: state.variables.map(v => 
      v.name === name 
        ? { 
            name: newData.name ?? v.name,
            value: newData.value ?? v.value
          } 
        : v
    )
  })),
  deleteVariable: (name) => set((state) => ({
    variables: state.variables.filter(v => v.name !== name)
  }))
}));

// Hook để sử dụng trong components React
export function useVariables() {
  const { variables, setVariables, addVariable, updateVariable, deleteVariable } = useVariablesStore();
  const [loading, setLoading] = useState(false);

  // Tạo biến mới
  const createVariable = useCallback(async (name: string, value: string = '') => {
    try {
      setLoading(true);
      
      // Kiểm tra nếu biến đã tồn tại
      if (variables.some(v => v.name === name)) {
        notifications.show({
          title: 'Lỗi',
          message: 'Biến với tên này đã tồn tại',
          color: 'red',
        });
        return null;
      }
      
      // Tạo biến mới
      const newVariable: Variable = {
        name,
        value
      };
      
      // Thêm biến mới vào store
      addVariable(newVariable);
      
      // Hiển thị thông báo thành công
      notifications.show({
        title: 'Thành công',
        message: 'Đã tạo biến mới',
        color: 'green',
      });
      
      return newVariable;
    } catch (err: any) {
      // Hiển thị thông báo lỗi
      notifications.show({
        title: 'Lỗi',
        message: 'Không thể tạo biến',
        color: 'red',
      });
      
      console.error('Error creating variable:', err);
      return null;
    } finally {
      setLoading(false);
    }
  }, [variables, addVariable]);

  // Cập nhật biến (sử dụng store)
  const updateVariableWrapped = useCallback(async (name: string, data: { name?: string; value?: string }) => {
    try {
      setLoading(true);
      
      // Cập nhật biến trong store
      updateVariable(name, data);
      
      // Hiển thị thông báo thành công
      notifications.show({
        title: 'Thành công',
        message: 'Đã cập nhật biến',
        color: 'green',
      });
      
      return true;
    } catch (err: any) {
      // Hiển thị thông báo lỗi
      notifications.show({
        title: 'Lỗi',
        message: 'Không thể cập nhật biến',
        color: 'red',
      });
      
      console.error('Error updating variable:', err);
      return false;
    } finally {
      setLoading(false);
    }
  }, [updateVariable]);

  // Xóa biến (sử dụng store)
  const deleteVariableWrapped = useCallback(async (name: string) => {
    try {
      setLoading(true);
      
      // Xóa biến khỏi store
      deleteVariable(name);
      
      // Hiển thị thông báo thành công
      notifications.show({
        title: 'Thành công',
        message: 'Đã xóa biến',
        color: 'green',
      });
      
      return true;
    } catch (err: any) {
      // Hiển thị thông báo lỗi
      notifications.show({
        title: 'Lỗi',
        message: 'Không thể xóa biến',
        color: 'red',
      });
      
      console.error('Error deleting variable:', err);
      return false;
    } finally {
      setLoading(false);
    }
  }, [deleteVariable]);

  return {
    variables,
    loading,
    setVariables,
    createVariable,
    updateVariable: updateVariableWrapped,
    deleteVariable: deleteVariableWrapped,
  };
} 